import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useMutation } from '@redux-requests/react';
import { TextArea } from 'component-library-and-tools';

// TODO: We should move the `LabeledContent` component to the CL if we want Safelist to be independent from WF.
import LabeledContent from 'shared/components/LabeledContent';
import Button from 'shared/components/Button';
import { extractTermsFromText, EXTRACT_TERMS_FROM_TEXT } from 'services/safelists/actions';
import { colors } from 'lib/prisma-styles';
import { DARK_THEME } from 'shared/constants';

import { TermRedactionAreaProps } from './types';

const StyledTermRedactionAreaContainer = styled.div`
  max-height: 100%;
  padding: 0 2.67rem 0 15.834rem;
  // It just looks better this way.
  background-color: ${({ theme }) => theme.mode === DARK_THEME ? colors.background.section : colors.background.sidebar};
`;

const StyledRedactionAreaHeader = styled.div`
  margin-bottom: 3.5rem;
`;

const StyledRedactionAreaTitle = styled.h4`
  margin: 2.167rem 0;
  font-size: 1.34rem;
  font-weight: 700;
`;

const StyledRedactionAreaContent = styled.div``;

const StyledTextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTextArea = styled(TextArea)`
  && {
    max-width: 33.34rem;
    height: 25.34rem;
    background-color: ${colors.interactive.defaultBackground};
    border-color: ${colors.interactive.defaultBorder};
    outline: 0;
  }
`;

const StyledTextAreaDescriptionList = styled.ul`
  margin: 0.67rem 0 0;
  padding: 0;
`;

const StyledTextAreaDescriptionItem = styled.li`
  list-style: none;

  &::before {
    content: '-';
    margin-right: 0.4167rem;
  }
`;

const StyledRedactionAreaFooter = styled.div`
  margin-top: 2.67rem;
`;

const StyledButton = styled(Button)`
  && {
    min-width: unset;
    height: 3.67rem;
    padding: 0 1.67rem;
    background-color: #d7dae0;
    color: #33373d;
    font-weight: 700;
  }
`;

const TermRedactionArea = ({
  className,
}: TermRedactionAreaProps) => {
  const dispatch = useDispatch();
  const [textToAnalyze, setTextToAnalyze] = useState('');
  const { loading } = useMutation({ type: EXTRACT_TERMS_FROM_TEXT });

  const onTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextToAnalyze(event.target.value);
  };

  const onButtonClick = useCallback(() => {
    dispatch(extractTermsFromText(textToAnalyze));
  }, [textToAnalyze]);

  return (
    <StyledTermRedactionAreaContainer className={className}>
      <StyledRedactionAreaHeader>
        <StyledRedactionAreaTitle>
          Type a string and we will extract the useful data
        </StyledRedactionAreaTitle>
      </StyledRedactionAreaHeader>
      <StyledRedactionAreaContent>
        <LabeledContent label='Data'>
          <StyledTextAreaContainer>
            <StyledTextArea onChange={onTextAreaChange} value={textToAnalyze} />
            <StyledTextAreaDescriptionList>
              <StyledTextAreaDescriptionItem>Paste or type a comma separated string</StyledTextAreaDescriptionItem>
              <StyledTextAreaDescriptionItem>Add IP range by network mask (123.54.21.23/24)</StyledTextAreaDescriptionItem>
            </StyledTextAreaDescriptionList>
          </StyledTextAreaContainer>
        </LabeledContent>
        <StyledRedactionAreaFooter>
          <StyledButton
            label='Analyze'
            isDisabled={!textToAnalyze}
            isLoading={loading}
            onClick={onButtonClick}
          />
        </StyledRedactionAreaFooter>
      </StyledRedactionAreaContent>
    </StyledTermRedactionAreaContainer>
  );
};

export { TermRedactionArea };
